// src/pages/CompanyPages/DashboardCalendar.js

import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  TextField,
  Button,
  Grid,
  Select,
  MenuItem,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";

import { useThemeMode } from "../../components/ThemeModeContext"; 
import axiosInstance from "../../Services/axiosConfig";
import Sidebar from "../../components/CompanySidebar"; 
import Footer from "../../components/Footer";
import FeedbackButton from "../../components/FeedbackButton";
import FeedbackSnackbar from "../../components/FeedbackSnackbar";

export default function DashboardCalendar() {
  const { companyId } = useParams();
  const token = localStorage.getItem("authToken");

  // Estados gerais
  const [loading, setLoading] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [sectors, setSectors] = useState([]);
  const [selectedSector, setSelectedSector] = useState("");

  // Snackbar de feedback (se precisar exibir mensagens ao usuário)
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Dark/Light Mode
  const { mode } = useThemeMode();

  // Cria o tema de forma dinâmica
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          background: {
            default: mode === "light" ? "#FAF5E4" : "#121212",
            paper: mode === "light" ? "#FFFFFF" : "#1E1E1E",
          },
          text: {
            primary: mode === "light" ? "#1A202C" : "#FFFFFF",
          },
        },
      }),
    [mode]
  );

  // Fecha o snackbar
  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  // Buscar info da empresa
  const fetchCompanyInfo = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`company-info/${token}/${companyId}/`);
      if (response.data.status === "success") {
        setCompanyInfo(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching company info", error);
    } finally {
      setLoading(false);
    }
  };

  // Buscar setores
  const fetchSectors = async () => {
    try {
      const response = await axiosInstance.get(`company-sectors/${token}/${companyId}/`);
      if (response.data.status === "success") {
        const sectorList = Object.entries(response.data.data).map(([id, sector]) => ({
          id,
          name: sector.name,
        }));
        setSectors(sectorList);
      }
    
    } catch (error) {
      console.error("Error fetching sectors", error);
    }
  };
  
  const fetchAppointments = async () => {
    setLoading(true);
    try {
      let endpoint;
      
      // Se não houver setor selecionado, chamamos rota que busca "todos" os setores
      if (selectedSector) {
        endpoint = `company-dash/appointments/${companyId}/${selectedSector}/${date}/`;
      } else {
        // Exemplo: rota fictícia para “todos os setores”
        endpoint = `company-dash/appointments/${companyId}/0/${date}/`;

      }
  
      const response = await axiosInstance.get(endpoint);
      if (response.data.status === "success") {
        setAppointments(Object.values(response.data.data));
      } else {
        setAppointments([]);
      }
    } catch (error) {
      console.error("Error fetching appointments", error);
    } finally {
      setLoading(false);
    }
  };
  // Mudar status
  const updateStatus = async (appointmentId, newStatus) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(
        `company-change-status/${token}/${appointmentId}/${companyId}/`,
        { status: newStatus }
      );
      if (response.data.status === "success") {
        fetchAppointments();
      }
    } catch (error) {
      console.error("Error updating appointment status", error);
    } finally {
      setLoading(false);
    }
  };

  // Cor do status
  const getStatusColor = (status) => {
    switch (status) {
      case "Agendado":
        return "#4caf50"; 
      case "Cancelado":
        return "#f44336"; 
      case "Realizado":
        return "#2196f3"; 
      case "No-show":
        return "#ff9800"; 
      default:
        return "#9e9e9e"; 
    }
  };

  // Formata data
  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" };
    return new Date(dateString).toLocaleString(undefined, options);
  };

  // Ao montar, busca info e setores
  useEffect(() => {
    fetchCompanyInfo();
    fetchSectors();
    fetchAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ao mudar setor ou data, busca agendamentos
  useEffect(() => {
    if (selectedSector) {
      fetchAppointments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSector, date]);

  return (
    <ThemeProvider theme={theme}>
      {/* Contêiner vertical (tela cheia) */}
      <Box display="flex" flexDirection="column" minHeight="100vh" bgcolor="background.default" color="text.primary">
        
        {/* Layout horizontal: Sidebar (esquerda) + Conteúdo (direita) */}
        <Box display="flex" flexGrow={1}>
          <Sidebar companyId={companyId} />

          {/* Conteúdo principal */}
          <Box flex={1} p={3}>
            <Typography variant="h4" gutterBottom>
              {companyInfo?.name || "Company Dashboard"}
            </Typography>

            {/* Filtros */}
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <TextField
                select
                label="Select Sector"
                value={selectedSector}
                onChange={(e) => setSelectedSector(e.target.value)}
                sx={{ width: 200 }}
              >
                {sectors.map((sector) => (
                  <MenuItem key={sector.id} value={sector.id}>
                    {sector.name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                label="Filter by Date"
                InputLabelProps={{ shrink: true }}
                sx={{ width: 180 }}
              />

              <Button
                variant="contained"
                color="primary"
                onClick={fetchAppointments}
                disabled={loading || !selectedSector}
                sx={{ minWidth: "120px" }}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : "Filter"}
              </Button>
            </Box>

            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
                <CircularProgress />
              </Box>
            ) : appointments.length > 0 ? (
              <Grid container spacing={2}>
                {appointments.map((appointment) => (
                  <Grid item xs={12} md={6} lg={4} key={appointment.id}>
                    <Box
                      p={2}
                      border={1}
                      borderColor="grey.300"
                      borderRadius={2}
                      boxShadow={2}
                    >
                      <Typography variant="h6" gutterBottom>
                        {appointment.client}
                      </Typography>
                      <Typography variant="body1">
                        Email: {appointment.email}
                      </Typography>
                      <Box mt={2} p={1} borderRadius={1} bgcolor={getStatusColor(appointment.status)}>
                        <Typography variant="body1" color="white" align="center">
                          Status: {appointment.status}
                        </Typography>
                      </Box>
                      <Typography variant="body1" mt={1}>
                        Date: {formatDate(appointment.date)}
                      </Typography>

                      <Select
                        value={appointment.status}
                        onChange={(e) => updateStatus(appointment.id, e.target.value)}
                        fullWidth
                        sx={{ mt: 2 }}
                      >
                        <MenuItem value="Agendado">Agendado</MenuItem>
                        <MenuItem value="Cancelado">Cancelado</MenuItem>
                        <MenuItem value="Realizado">Realizado</MenuItem>
                        <MenuItem value="No-show">No-show</MenuItem>
                      </Select>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body1" mt={2}>
                No appointments found for this sector and date.
              </Typography>
            )}
          </Box>
        </Box>

        {/* Botão de Feedback (DarkMode + Feedback) */}
        <FeedbackButton />

        {/* Footer no final da página */}
        <Footer />

        {/* Exemplo de Snackbar para feedback */}
        <FeedbackSnackbar
          open={openSnackbar}
          handleClose={() => setOpenSnackbar(false)}
          message={snackbarMsg}
          severity={snackbarSeverity}
        />
      </Box>
    </ThemeProvider>
  );
}
